import { Link } from "react-router-dom";
import { ArrowUpRight, Star, ExternalLink } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, BOOKS } from "../data/siteContent";
import { BOOKS_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, book12LawsSchema, webPageSchema, breadcrumbSchema } from "../seo/schemas";

export default function Books() {
  return (
    <SiteLayout>
      <Seo
        title="Books by Manuel Santana — The 12 Laws of Dating Dynamics"
        description="The 12 Laws of Dating Dynamics (2025) by Manuel Santana. Reviews, excerpts and purchase links."
        path="/books"
        schemas={buildSchemas([
          book12LawsSchema,
          webPageSchema({ path: "/books", name: "Books by Manuel Santana", description: "The 12 Laws of Dating Dynamics (2025) by Manuel Santana. Reviews, excerpts and purchase links." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Books", path: "/books" }]),
        ])}
      />
      <PageHeader
        overline="Books — Author Page"
        title="Manuel Santana"
        italic="— in print."
        kicker="The 2025 title. A growing body of work on relationships, identity and modern life."
      />

      <section data-testid={BOOKS_T.section} className="bg-white py-20 md:py-28 space-y-32">
        {BOOKS.map((b, idx) => (
          <div key={b.title} className="mx-auto max-w-[1600px] px-6 md:px-12">
            <div className="grid grid-cols-12 gap-6 lg:gap-16 items-start">
              <div className={`col-span-12 lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
                  <img src={idx === 0 ? ASSETS.bookPortrait : ASSETS.aboutPortrait} alt={b.title} draggable={false} />
                </div>
              </div>
              <div className={`col-span-12 lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                <span className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">{b.released}</span>
                <h2 className="mt-5 font-display text-[#0a0a0a] text-[clamp(34px,5.5vw,76px)] leading-[1.02] tracking-[-0.02em]">
                  {b.title}
                </h2>
                <p className="mt-6 font-display font-light text-[#0a0a0a] text-xl md:text-2xl leading-snug">{b.subtitle}</p>
                <p className="mt-6 text-black/65 text-base md:text-lg leading-relaxed">{b.description}</p>

                {b.reviews.length > 0 && (
                  <div className="mt-10 space-y-5">
                    <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Press Reviews</p>
                    {b.reviews.map((r) => (
                      <div key={r.source} className="border-l-2 border-[#C8102E] pl-5">
                        <div className="flex items-center gap-1 text-[#C8102E] mb-2">
                          {Array.from({ length: r.stars }).map((_, i) => (
                            <Star key={i} size={12} fill="#C8102E" />
                          ))}
                        </div>
                        <p className="font-display font-light text-[#0a0a0a] text-lg">"{r.text}"</p>
                        <p className="mt-1 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-black/50">— {r.source}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-10">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">— Where To Buy</p>
                  {b.officialSite && (
                    <a
                      href={b.officialSite}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={BOOKS_T.purchase(`${idx}-official-site`)}
                      className="group relative inline-flex items-center justify-between gap-6 w-full sm:w-auto sm:min-w-[420px] bg-[#C8102E] hover:bg-[#0a0a0a] text-white px-6 py-5 mb-4 transition-all"
                    >
                      <span className="flex flex-col items-start gap-1">
                        <span className="font-mono-ui text-[9px] tracking-[0.3em] uppercase text-white/70">— Official Book Site</span>
                        <span className="font-display text-xl md:text-2xl tracking-tight leading-none">the12lawsofdatingdynamics.com</span>
                      </span>
                      <ExternalLink size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {b.purchaseLinks.map((p) => (
                      <a
                        key={p.label}
                        href={p.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={BOOKS_T.purchase(`${idx}-${p.label.toLowerCase().replace(/\s/g, '-')}`)}
                        className="inline-flex items-center gap-2 border border-black/30 hover:bg-[#0a0a0a] hover:text-white px-4 py-3 font-mono-ui text-[11px] tracking-[0.2em] uppercase transition-all"
                      >
                        {p.label} <ArrowUpRight size={14} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-white py-20 md:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Author Inquiries</p>
            <h3 className="mt-5 font-display text-[#0a0a0a] text-[clamp(32px,5vw,60px)] leading-tight tracking-[-0.02em]">
              Events, signings, festivals <span className="font-light text-black/45">& long-form interviews.</span>
            </h3>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:text-right">
            <Link to="/contact" className="btn-primary">Contact Manuel's Team</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
